import { Segmented } from "antd";
import { Link } from "react-router-dom";

/** @const {string} */
const SCOPE_STORAGE_PREFIX = "operationsTodayScope";

/**
 * Last Mine/Everyone choice for this person, or null.
 *
 * @param {*} userId
 * @return {?string}
 */
export const readTodayScope = userId => {
  try {
    const stored = window.localStorage.getItem(`${SCOPE_STORAGE_PREFIX}:${userId}`);
    return stored === "mine" || stored === "all" ? stored : null;
  } catch {
    return null;
  }
};

/**
 * @param {*} userId
 * @param {string} scope "mine" or "all".
 */
export const saveTodayScope = (userId, scope) => {
  try {
    window.localStorage.setItem(`${SCOPE_STORAGE_PREFIX}:${userId}`, scope);
  } catch {
    // Storage blocked: the switch still works for this visit.
  }
};

const plural = (count, one, many) => (count === 1 ? one : many);

/**
 * Chips for the Today strip. Hidden areas come back as null from the API and
 * are left out; zero counts are left out so the strip only lists real work.
 *
 * @param {!Object} today `today` block from the control-centre API.
 * @return {!Array<{key: string, label: string, to?: string, tone: string}>}
 */
export const buildTodayChips = today => {
  const mine = today.scope === "mine";
  const scopeParam = `&scope=${today.scope}`;
  const dueToday = today.sales_due_today;
  const chips = [
    dueToday > 0 && {
      key: "sales_due_today",
      label: `${dueToday} ${mine ? "of your " : ""}${plural(
        dueToday,
        "sales order",
        "sales orders",
      )} to confirm, shipping today`,
      to: `/dashboard?section=sales&due=today${scopeParam}`,
      tone: "info",
    },
    today.sales_overdue > 0 && {
      key: "sales_overdue",
      label: `${today.sales_overdue} overdue ${plural(
        today.sales_overdue,
        "sales order",
        "sales orders",
      )} to confirm`,
      to: `/dashboard?section=sales&due=overdue${scopeParam}`,
      tone: "warning",
    },
    today.purchases_arriving_today > 0 && {
      key: "purchases_arriving_today",
      label: `${today.purchases_arriving_today} ${plural(
        today.purchases_arriving_today,
        "purchase order",
        "purchase orders",
      )} to confirm, due today`,
      to: "/dashboard?section=purchases&due=today",
      tone: "info",
    },
    today.receipts_waiting > 0 && {
      key: "receipts_waiting",
      label: `${today.receipts_waiting} ${plural(
        today.receipts_waiting,
        "receipt",
        "receipts",
      )} waiting for approval`,
      to: "/dashboard?section=receipts",
      tone: "info",
    },
    today.sync_failures > 0 && {
      key: "sync_failures",
      label: `${today.sync_failures} ${plural(
        today.sync_failures,
        "sync failed",
        "syncs failed",
      )} this week`,
      to: "/integration/sync_history",
      tone: "danger",
    },
    today.my_tasks_due_today > 0 && {
      key: "my_tasks_due_today",
      label: `${today.my_tasks_due_today} of my ${plural(
        today.my_tasks_due_today,
        "task",
        "tasks",
      )} due`,
      tone: "info",
    },
  ];
  return chips.filter(Boolean);
};

/**
 * "What do I need to deal with today?" line at the top of the home screen.
 *
 * @param {{
 *   today: ?Object,
 *   onScopeChange: function(string),
 *   onOpenTasks: function(),
 * }} props
 * @return {?JSX.Element}
 */
export function OperationsTodayStrip({ today, onScopeChange, onOpenTasks }) {
  if (!today || !today.scope) return null;
  const chips = buildTodayChips(today);

  return (
    <section className="operations-today" aria-label="Today" data-guide="operations.today">
      <div className="operations-today-head">
        <h2>Today</h2>
        {today.can_filter_mine ? (
          <span data-guide="operations.today.scope">
            <Segmented
              size="small"
              value={today.scope}
              onChange={onScopeChange}
              options={[
                { label: "Mine", value: "mine" },
                { label: "Everyone", value: "all" },
              ]}
            />
          </span>
        ) : null}
      </div>
      {chips.length ? (
        <ul className="operations-today-chips">
          {chips.map(chip => (
            <li key={chip.key}>
              {chip.to ? (
                <Link className={`operations-today-chip is-${chip.tone}`} to={chip.to}>
                  {chip.label}
                </Link>
              ) : (
                <button
                  type="button"
                  className={`operations-today-chip is-${chip.tone}`}
                  onClick={onOpenTasks}
                >
                  {chip.label}
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="operations-today-clear">
          All caught up — nothing overdue today. Enjoy it while it lasts.
        </p>
      )}
    </section>
  );
}
